import React, { useEffect, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useGuests } from 'hooks/usecase/useGuests';
import DeleteModal from './components/DeleteModal';
import DataGrid from 'ui/components/DataGrid';
import dayjs from 'dayjs';
import EditModal from 'ui/components/users/EditGuestModal';
import validation from '../home/admin/companies/components/users/validators/user.validator';
import MenteeDetailModal from 'ui/components/shared/MenteeDetailModal/MenteeDetailModal';
import { useMeQuery } from 'hooks/queries';
import { userModalNextPaginationManagement, userModalPreviousPaginationManagement } from 'utils/pagination';

// Configs
import { tableSchemaBase as columns } from './Coachees.config';

const Coachees = ({ role, typeCoachees = '!RH_Validation', noDataDescription = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [queryParams, setQueryParams] = useState({ page: 1, size: 10, type: typeCoachees });
  const [guestToDelete, setGuestToDelete] = useState(null);
  const { data: user } = useMeQuery();

  useEffect(() => {
    setQueryParams({ page: 1, size: 10, type: typeCoachees });
  }, [typeCoachees]);

  // Hooks
  const {
    guests,
    control,
    isLoading,
    isFetched,
    totalPages,
    totalItems,
    handleDeleteGuest,
    scopes,
    isOpenEditModal,
    setIsOpenEditModal,
    setEditingGuest,
    isEditLoading,
    onEdit,
    onValidateGuestSelfRegistration,
    reset,
    watch,
    companyId,
    menteeIdDetailsSelected,
    setMenteeIdDetailsSelected,
  } = useGuests(queryParams);

  const [modalCoachActionType, setModalCoachActionType] = useState('');

  const handleOnFilter = (filters, currentPage, currentSize) => {
    let queryParamsFilter = {};
    if (filters.items) {
      filters.items.map((filter) => {
        switch (filter.field) {
          case 'scopeName':
            queryParamsFilter = { ...queryParamsFilter, scopeId: filter.value };
            break;
          case 'user':
            // eslint-disable-next-line no-case-declarations
            let queryParamsFilterName = {};
            if (filter && filter.value && filter.value.length > 0) {
              var [premierMot, ...resteChaine] = filter.value.split(' ');
              if (resteChaine.length > 0) {
                queryParamsFilterName = { firstname: premierMot, lastname: resteChaine.join(' ') };
              } else {
                queryParamsFilterName = { firstname: premierMot };
              }
            }
            queryParamsFilter = { ...queryParamsFilter, ...queryParamsFilterName };
            break;
          case 'registrationDate':
            queryParamsFilter = {
              ...queryParamsFilter,
              createdAt: filter.value && dayjs(filter.value).format('YYYY-MM-DD'),
            };
            break;
          default:
            queryParamsFilter = { ...queryParamsFilter, [filter.field]: filter.value };
            break;
        }
      });
    }

    setQueryParams({
      ...{ orderBy: queryParams.orderBy, orderDirection: queryParams.orderDirection },
      page: currentPage,
      size: currentSize,
      type: typeCoachees,
      ...queryParamsFilter,
    });
  };

  const handleOnSort = (sort, currentPage, currentSize) => {
    let queryParamsSort = {};
    switch (sort.orderBy) {
      case 'scopeName':
        queryParamsSort = { ...sort, orderBy: 'scopeId' };
        break;
      case 'user':
        queryParamsSort = { ...sort, orderBy: 'firstname' };
        break;
      case 'createdAt':
        queryParamsSort = { ...sort, orderBy: 'createdAt' };
        break;
      case '':
        queryParamsSort = { orderBy: '' };
        break;
      default:
        queryParamsSort = { ...sort };
        break;
    }
    setQueryParams({ ...queryParams, page: currentPage, size: currentSize, type: typeCoachees, ...queryParamsSort });
  };

  useEffect(() => {
    if (!!menteeIdDetailsSelected && guests.length && !isLoading) {
      if (modalCoachActionType === 'next') {
        setMenteeIdDetailsSelected(guests?.[0]?.id);
      } else {
        setMenteeIdDetailsSelected(guests?.[guests.length - 1]?.id);
      }
    } else if (!guests.length && !isLoading) {
      setMenteeIdDetailsSelected(null);
    }
  }, [queryParams.page, isLoading]);

  const showNoData = noDataDescription && queryParams.page === 1 && guests.length === 0;

  return (
    <Stack sx={{ minHeight: '630px', backgroundColor: 'white', padding: '10px 38px' }}>
      {!showNoData && (
        <DataGrid
          key={typeCoachees}
          columns={columns}
          rows={guests}
          role={role}
          totalItems={totalItems}
          onEdit={(item) => {
            setEditingGuest(item);
            setIsOpenEditModal(true);
          }}
          onDelete={(item) => {
            setIsOpen(true);
            setGuestToDelete(item);
          }}
          onFilter={handleOnFilter}
          onSort={handleOnSort}
          onPaginationChange={(page, nbrPages) =>
            setQueryParams({ ...queryParams, page: page, size: nbrPages, type: typeCoachees })
          }
          totalPages={totalPages ? totalPages : 0}
          loading={isLoading}
          currentPage={queryParams.page}
          onRowClicked={(item) => {
            setEditingGuest(item.row);
            setMenteeIdDetailsSelected(item.id);
          }}
        />
      )}

      {showNoData && (
        <Box sx={{ alignItems: 'center', justifyContent: 'center', display: 'flex', height: '100%' }}>
          <Typography sx={{ fontSize: '18px', fontWeight: 500 }}>{noDataDescription}</Typography>
        </Box>
      )}

      {/** Modal to edit */}
      <EditModal
        title={'Edit '}
        control={control}
        isOpen={isOpenEditModal}
        onClose={() => {
          reset();
          setIsOpenEditModal(false);
        }}
        onEdit={onEdit}
        validation={validation}
        watch={watch}
        companyId={companyId}
        isEditLoading={isEditLoading}
      />
      {/** Modal to delete */}
      <DeleteModal
        itemToDelete={guestToDelete}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onValidate={() => {
          handleDeleteGuest({ id: guestToDelete.id });
          setIsOpen(false);
        }}
      />
      {menteeIdDetailsSelected && !isLoading && (
        <MenteeDetailModal
          user={guests && guests.find((item) => item.id === menteeIdDetailsSelected)}
          company={user.company}
          open={!!menteeIdDetailsSelected}
          onClose={() => setMenteeIdDetailsSelected(false)}
          directionalArrowsActivated={!!guests.length}
          editable
          control={control}
          validation={validation}
          onClickPreviousMentee={() => {
            setModalCoachActionType('previous');
            const coachIndex = guests.findIndex((item) => item.id === menteeIdDetailsSelected);

            userModalPreviousPaginationManagement({
              userIndex: coachIndex,
              users: guests,
              totalPages,
              queryParams,
              setUserIdSelected: setMenteeIdDetailsSelected,
              setQueryParams,
            });
          }}
          onClickNextMentee={() => {
            setModalCoachActionType('next');
            const coachIndex = guests.findIndex((item) => item.id === menteeIdDetailsSelected);

            userModalNextPaginationManagement({
              userIndex: coachIndex,
              users: guests,
              totalPages,
              queryParams,
              setUserIdSelected: setMenteeIdDetailsSelected,
              setQueryParams,
            });
          }}
          onSave={() => {
            onEdit();
            setMenteeIdDetailsSelected(null);
          }}
          onValidateGuestSelfRegistration={() => {
            onValidateGuestSelfRegistration(menteeIdDetailsSelected);
          }}
        />
      )}
    </Stack>
  );
};

export default Coachees;
